import React from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatMoneyPl } from '../../../utils/currency';
import { getTransportLabel } from '../../../utils/transportLabel';
import { commercialAllocationMethodLabelKey, type AllocationMethod } from '../../../utils/transportCostAllocation';
import ProductNameWithDimensions, { type ProductDimensionLine } from '../../../components/products/ProductNameWithDimensions';

interface SupplierOrderTruckFreightListProps {
  transports: SupplierOrderTransportDto[];
  onOpenTransport: (transportId: string) => void;
  /** Short rows for the side card of the ZD stage panel: a truck per block, one fact per line. */
  compact?: boolean;
  /** ZD lines of the card (a freight line id is the ZD line id): their dimensions under the name. */
  items?: ProductDimensionLine[];
}

/**
 * Usability round 2026-10-05: what each truck costs this ZD (backend figures,
 * returned only to transport-cost roles): the truck's price, this ZD's share, the
 * cost per 1 m², the split key and, when the lines are listed, the same per product.
 */
export default function SupplierOrderTruckFreightList({ transports, onOpenTransport, compact = false, items }: SupplierOrderTruckFreightListProps) {
  const { t } = useTranslation(['transport']);
  const withFreight = transports.filter((transport) => transport.freight);
  if (withFreight.length === 0) return null;

  return (
    <ul
      className={`supplier-order-truck-freight${compact ? ' is-compact' : ''}`}
      data-testid="supplier-order-truck-freight"
      style={compact ? undefined : { margin: 0, paddingLeft: 18 }}
    >
      {withFreight.map((transport) => {
        const freight = transport.freight!;
        const foreign = freight.currency !== 'PLN';
        const showLines = freight.lines.length > 1 || (freight.lines.length === 1 && freight.lines[0].perM2PLN !== null);
        const perM2 = freight.perM2PLN === null
          ? null
          : (foreign && freight.perM2 !== null
            ? `${formatMoneyPl(freight.perM2, freight.currency)} · ${formatMoneyPl(freight.perM2PLN, 'PLN')}`
            : formatMoneyPl(freight.perM2PLN, 'PLN'));
        const money = (pln: number | null, inTruck: number | null) => (pln === null
          ? '—'
          : (foreign && inTruck !== null
            ? `${formatMoneyPl(inTruck, freight.currency)} · ${formatMoneyPl(pln, 'PLN')}`
            : formatMoneyPl(pln, 'PLN')));
        const keyLabel = !freight.allocationKey
          ? null
          : freight.allocationKey === 'BY_COMMERCIAL_QTY'
            ? t('transport:supplierOrderFreight.defaultKey')
            : t(`transport:${commercialAllocationMethodLabelKey(freight.allocationKey as AllocationMethod)}`);
        const truckCost = t('transport:supplierOrderFreight.truckCost', {
          amount: freight.transportRate === null ? '—' : formatMoneyPl(freight.transportRate, freight.currency),
        });
        const thisOrder = freight.amountPLN !== null ? t('transport:supplierOrderFreight.thisOrder', {
          amount: formatMoneyPl(freight.amountPLN, 'PLN'),
          percent: freight.percent === null ? '—' : freight.percent.toLocaleString('pl-PL', { maximumFractionDigits: 2 }),
        }) : null;
        // Round 3 (U18): per product when the lines are listed; else the truck average, named so.
        const average = perM2 && !showLines ? t('transport:supplierOrderFreight.perM2Average', { amount: perM2 }) : null;
        const key = keyLabel ? t('transport:supplierOrderFreight.key', { key: keyLabel }) : null;
        const lines = showLines ? freight.lines.map((line) => (
          <li key={line.id} data-testid="supplier-order-truck-freight-line">
            <ProductNameWithDimensions name={`${line.name}:`} dimensions={items?.find((item) => item.id === line.id)}
              dimensionsTestId="supplier-order-truck-freight-line-dimensions" />{' '}
            {money(line.amountPLN, line.amountPLN !== null && freight.exchangeRate ? Math.round((line.amountPLN / freight.exchangeRate) * 100) / 100 : null)}
            {line.perM2PLN !== null ? ` · ${t('transport:supplierOrderFreight.perM2', { amount: money(line.perM2PLN, line.perM2) })}` : ''}
          </li>
        )) : null;

        if (compact) {
          return (
            <li key={transport.id} className="zd-truck" data-testid="supplier-order-truck-freight-row">
              <Button type="link" size="small" className="zd-truck__label" onClick={() => onOpenTransport(transport.id)}>
                {getTransportLabel(transport)}
              </Button>
              <div className="zd-truck__line">{truckCost}</div>
              {thisOrder ? <div className="zd-truck__line">{thisOrder}</div> : null}
              {average || key ? <div className="zd-truck__line">{[average, key].filter(Boolean).join(' · ')}</div> : null}
              {lines ? <ul className="zd-truck__lines">{lines}</ul> : null}
            </li>
          );
        }
        return (
          <li key={transport.id} data-testid="supplier-order-truck-freight-row">
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenTransport(transport.id)}>
              {getTransportLabel(transport)}
            </Button>
            {': '}
            {truckCost}
            {thisOrder ? ` · ${thisOrder}` : ''}
            {average ? ` · ${average}` : ''}
            {key ? ` · ${key}` : ''}
            {lines ? <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{lines}</ul> : null}
          </li>
        );
      })}
    </ul>
  );
}
